import React, { useState } from 'react';
import {
  Download, Eye, Edit3, Trash2, Clock, User, AlertTriangle,
  Check, X, Plus, FileText, ChevronRight
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormat } from '../../i18n/format.js';
import { apiRequest } from '../../api/core.js';

// Icône, couleur et article restent constants ; le libellé se résout au rendu par t().
const REQUEST_TYPES = {
  access: { icon: Eye, color: 'blue', article: 'Art. 15' },
  rectification: { icon: Edit3, color: 'amber', article: 'Art. 16' },
  erasure: { icon: Trash2, color: 'red', article: 'Art. 17' },
  portability: { icon: Download, color: 'green', article: 'Art. 20' },
  restriction: { icon: Clock, color: 'purple', article: 'Art. 18' },
  objection: { icon: X, color: 'gray', article: 'Art. 21' }
};

const STATUS_CONFIG = {
  pending: { color: 'bg-amber-100 text-amber-800' },
  in_progress: { color: 'bg-blue-100 text-blue-800' },
  completed: { color: 'bg-green-100 text-green-800' },
  rejected: { color: 'bg-red-100 text-red-800' },
  partially_completed: { color: 'bg-purple-100 text-purple-800' }
};

const DATA_TYPES = ['personal_info', 'location', 'performance', 'telematics', 'claims', 'trips'];

function DriverDataRequests({ onMessage }) {
  const { t, fmt } = useFormat();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    driver_id: '',
    request_type: 'access',
    requested_data_types: [],
    request_description: ''
  });

  const { data: driversData } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => apiRequest('/driver')
  });

  // Fetch data requests (tenant-isolated via middleware)
  const { data: requestsData, isLoading } = useQuery({
    queryKey: ['data_requests'],
    queryFn: () => apiRequest('/gdpr-compliance/data-requests')
  });

  // Create data request (org_id injected from JWT by middleware)
  const createMutation = useMutation({
    mutationFn: (data) => apiRequest('/gdpr-compliance/data-requests', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['data_requests']);
      setShowForm(false);
      onMessage?.({ type: 'success', text: t('compliance.dataRequests.toast.created') });
    }
  });

  // Update data request (ownership verified by middleware)
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => apiRequest(`/gdpr-compliance/data-requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['data_requests']);
      setSelectedRequest(null);
      onMessage?.({ type: 'success', text: t('compliance.dataRequests.toast.updated') });
    }
  });

  const drivers = driversData?.data || [];
  const requests = requestsData?.data || [];

  const filteredRequests = filterStatus === 'all'
    ? requests
    : requests.filter(r => r.status === filterStatus);

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? `${driver.first_name} ${driver.last_name}` : t('compliance.dataRequests.unknownDriver');
  };

  const getDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleStatusUpdate = (requestId, newStatus) => {
    updateMutation.mutate({ id: requestId, data: { status: newStatus } });
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const overdueCount = requests.filter(r => {
    const days = getDaysRemaining(r.due_date);
    return days !== null && days < 0 && r.status !== 'completed';
  }).length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title={t('compliance.dataRequests.stats.pending')}
          value={pendingCount}
          color="amber"
          icon={Clock}
        />
        <StatCard
          title={t('compliance.dataRequests.stats.overdue')}
          value={overdueCount}
          color="red"
          icon={AlertTriangle}
        />
        <StatCard
          title={t('compliance.dataRequests.stats.completedThisMonth')}
          value={requests.filter(r => r.status === 'completed').length}
          color="green"
          icon={Check}
        />
        <StatCard
          title={t('compliance.dataRequests.stats.total')}
          value={requests.length}
          color="blue"
          icon={FileText}
        />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('compliance.dataRequests.title')}</h3>
          <p className="text-sm text-gray-500">
            {t('compliance.dataRequests.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('compliance.dataRequests.newButton')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2">
        <span className="text-sm text-gray-500">{t('compliance.dataRequests.filters.label')}</span>
        {['all', 'pending', 'in_progress', 'completed', 'rejected'].map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-3 py-1 rounded-full text-sm ${
              filterStatus === status
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? t('compliance.dataRequests.filters.all') : t(`compliance.dataRequests.status.${status}`, { defaultValue: status })}
          </button>
        ))}
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('common.states.loading')}</div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('compliance.dataRequests.empty')}</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredRequests.map(request => {
              const daysRemaining = getDaysRemaining(request.due_date);
              const isOverdue = daysRemaining !== null && daysRemaining < 0 && request.status !== 'completed';
              const typeKey = REQUEST_TYPES[request.request_type] ? request.request_type : 'access';
              const requestType = REQUEST_TYPES[typeKey];
              const TypeIcon = requestType.icon;

              return (
                <div
                  key={request.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${isOverdue ? 'border-l-4 border-red-500' : ''}`}
                  onClick={() => setSelectedRequest(request)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${requestType.color}-100 text-${requestType.color}-600`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{t(`compliance.dataRequests.types.${typeKey}`)}</span>
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{requestType.article}</span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          {getDriverName(request.driver_id)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_CONFIG[request.status]?.color || 'bg-gray-100'}`}>
                          {t(`compliance.dataRequests.status.${request.status}`, { defaultValue: request.status })}
                        </span>
                        {daysRemaining !== null && request.status !== 'completed' && (
                          <div className={`text-xs mt-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            {isOverdue
                              ? t('compliance.dataRequests.overdueBy', { count: Math.abs(daysRemaining) })
                              : t('compliance.dataRequests.daysRemaining', { count: daysRemaining })}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  {request.request_description && (
                    <p className="mt-2 text-sm text-gray-600 ml-14">
                      {request.request_description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* GDPR Rights Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4">{t('compliance.dataRequests.typesTitle')}</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(REQUEST_TYPES).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <div key={key} className="border rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Icon className={`w-5 h-5 mr-2 text-${config.color}-600`} />
                  <span className="font-medium">{t(`compliance.dataRequests.types.${key}`)}</span>
                </div>
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{config.article}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('compliance.dataRequests.form.title')}</h3>
              <button onClick={() => setShowForm(false)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.dataRequests.form.driver')}</label>
                <select
                  value={formData.driver_id}
                  onChange={e => setFormData({ ...formData, driver_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">{t('common.actions.select')}</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.dataRequests.form.type')}</label>
                <select
                  value={formData.request_type}
                  onChange={e => setFormData({ ...formData, request_type: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {Object.entries(REQUEST_TYPES).map(([key, config]) => (
                    <option key={key} value={key}>{t(`compliance.dataRequests.types.${key}`)} ({config.article})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.dataRequests.form.dataTypes')}</label>
                <div className="space-y-2">
                  {DATA_TYPES.map(type => (
                    <label key={type} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.requested_data_types.includes(type)}
                        onChange={e => {
                          const types = e.target.checked
                            ? [...formData.requested_data_types, type]
                            : formData.requested_data_types.filter(t => t !== type);
                          setFormData({ ...formData, requested_data_types: types });
                        }}
                        className="mr-2"
                      />
                      <span>{t(`compliance.dataRequests.dataTypes.${type}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.dataRequests.form.description')}</label>
                <textarea
                  value={formData.request_description}
                  onChange={e => setFormData({ ...formData, request_description: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="3"
                  placeholder={t('compliance.dataRequests.form.descriptionPlaceholder')}
                />
              </div>

              <div className="border-t pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('compliance.dataRequests.form.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {t('compliance.dataRequests.detail.title', { id: selectedRequest.id })}
              </h3>
              <button onClick={() => setSelectedRequest(null)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500">{t('compliance.dataRequests.detail.driver')}</span>
                  <p className="font-medium">{getDriverName(selectedRequest.driver_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">{t('compliance.dataRequests.detail.type')}</span>
                  <p className="font-medium">
                    {t(`compliance.dataRequests.types.${selectedRequest.request_type}`, { defaultValue: selectedRequest.request_type })}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">{t('compliance.dataRequests.detail.requestDate')}</span>
                  <p className="font-medium">
                    {fmt.date(selectedRequest.request_date)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">{t('compliance.dataRequests.detail.dueDate')}</span>
                  <p className="font-medium">
                    {fmt.date(selectedRequest.due_date)}
                  </p>
                </div>
              </div>

              {selectedRequest.request_description && (
                <div>
                  <span className="text-sm text-gray-500">{t('compliance.dataRequests.detail.description')}</span>
                  <p className="mt-1">{selectedRequest.request_description}</p>
                </div>
              )}

              <div className="border-t pt-4">
                <span className="text-sm text-gray-500 block mb-2">{t('compliance.dataRequests.detail.updateStatus')}</span>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                    <button
                      key={status}
                      onClick={() => handleStatusUpdate(selectedRequest.id, status)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        selectedRequest.status === status
                          ? config.color + ' font-medium'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t(`compliance.dataRequests.status.${status}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, color, icon: Icon }) {
  const colorClasses = {
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default DriverDataRequests;
